import {customerPackageScope} from '../../../shared/customerPackageScope';
import './PackageScopeDetails.css';
/** Present included work and quantities without internal line-item prices. */
export function organizePackageScope(scope: string[]) {
  const groups = new Map<string, Map<string, number>>();
  for (const line of customerPackageScope(scope)) {
    const match = line.match(/^([^:]+):\s+(.+)$/);
    const isRoom =
      match &&
      !/^(Delivery|Installation|Finish|Frame|Professional|Includes)/i.test(
        match[1],
      );
    const heading = isRoom ? match![1] : "Package services & specifications";
    const text = isRoom ? match![2] : line;
    const rows = groups.get(heading) ?? new Map<string, number>();
    rows.set(text, (rows.get(text) ?? 0) + 1);
    groups.set(heading, rows);
  }
  return [...groups].map(([heading, rows]) => ({
    heading,
    rows: [...rows].map(([text, count]) => ({ text, count })),
  }));
}

const scopeRank = (text:string) => /over.+bunk/i.test(text) ? 0 : /guardrail/i.test(text) ? 1 : /staircase|ladder/i.test(text) ? 2 : 3;
const categories = ['Configuration', 'Finish', 'Access & storage', 'Lighting', 'Other specifications'];
export function organizeRoomScope(scope: string[]) {
  const rooms = new Map<string, {text:string; count:number; bunk:string; category:string}[]>();
  for (const group of organizePackageScope(scope)) {
    const match = group.heading.match(/^(.*), bunk (\d+)$/i);
    const room = match ? match[1] : group.heading;
    const rows = rooms.get(room) ?? [];
    for (const row of group.rows) {
      const text = row.text;
      const category = /light|puck|sconce|electrical/i.test(text) ? 'Lighting'
        : /stair|ladder|drawer|storage|niche|shelf|shelves/i.test(text) ? 'Access & storage'
        : /gloss|matte|custom colo[u]?r|paint|finish/i.test(text) ? 'Finish'
        : /bunk|guardrail|tower|soffit|frame/i.test(text) ? 'Configuration' : 'Other specifications';
      rows.push({...row, bunk:match ? `Bunk ${match[2]}` : '', category});
    }
    rooms.set(room, rows);
  }
  return [...rooms].sort(([a],[b]) => Number(a==='Package services & specifications')-Number(b==='Package services & specifications'))
    .map(([heading, rows]) => ({heading, categories:categories.map(label => ({label, rows:rows.filter(row=>row.category===label).sort((a,b)=>scopeRank(a.text)-scopeRank(b.text))})).filter(group=>group.rows.length)}));
}
function ScopeRow({text,count,bunk}:{text:string;count:number;bunk?:string}) {
  const parts=text.split(' · '), first=parts.shift()!;
  if(parts[0]?.replace(/^\d+\s*[×x]\s*/, '')===first){
    const quantity=Number(parts[0].match(/^\d+/)?.[0]||1);
    if(quantity===1)parts.shift();else parts[0]=`Quantity: ${quantity}`;
  }
  return <li className="scope-detail-row">
    {bunk&&<span className="scope-bunk-label">{bunk}</span>}
    <strong>{count>1?`${count} × `:''}{first}</strong>
    {!!parts.length&&<dl>{parts.map((part,index)=><div className="scope-detail-note" key={index}><dd>{part}</dd></div>)}</dl>}
  </li>;
}
export default function PackageScopeDetails({scope,organized=false}:{scope:string[];organized?:boolean}) {
  if(organized)return <div className="scope-details scope-organized">{organizeRoomScope(scope).map(room=><section className="scope-room" key={room.heading}>
    <h4>{room.heading==='Package services & specifications'?'Services & specifications':room.heading}</h4>
    {room.categories.map(group=><section className="scope-category" key={group.label}><h5>{group.label}</h5><ul>{group.rows.map((row,i)=><ScopeRow key={i} {...row}/>)}</ul></section>)}
  </section>)}</div>;
  return (
    <div className="scope-details">
      {organizePackageScope(scope).map((group) => (
        <section className="scope-details-group" key={group.heading}>
          <h4>{group.heading}</h4>
          <ul>
            {group.rows.map(({ text, count }) => {
              const parts = text.split(" · ");
              const first = parts.shift()!;
              const pending = parts.some((part) =>
                /pending|excluded from this estimate/i.test(part),
              );
              // The model describes the same named item twice when its detail starts with a quantity.
              if (parts[0]?.replace(/^\d+\s*[×x]\s*/, "") === first) {
                const quantity = Number(parts[0].match(/^\d+/)?.[0] || 1);
                if (quantity === 1) parts.shift();
                else parts[0] = `Quantity: ${quantity}`;
              }
              return (
                <li
                  key={text}
                  className={
                    pending
                      ? "scope-detail-row scope-detail-pending"
                      : "scope-detail-row"
                  }
                >
                  <strong>
                    {count > 1 ? `${count} × ` : ""}
                    {first}
                  </strong>
                  {!!parts.length && (
                    <dl>
                      {parts.map((part, index) => {
                        const colon = part.indexOf(": ");
                        return colon > 0 ? (
                          <div key={index}>
                            <dt>{part.slice(0, colon)}</dt>
                            <dd>{part.slice(colon + 2)}</dd>
                          </div>
                        ) : (
                          <div className="scope-detail-note" key={index}>
                            <dd>{part}</dd>
                          </div>
                        );
                      })}
                    </dl>
                  )}
                </li>
              );
            })}
          </ul>
        </section>
      ))}
    </div>
  );
}
